import { signal, type Signal } from '@preact/signals';

import type { Api, CheckRequest } from './api';
import type { LocalOutcome } from './offline/local-check';
import type { Candidate, CheckMode, CheckReason, CheckResult, PersonBrief } from './types';

/** Was die Ergebnisanzeige braucht. Von Server und Gerät gleichermassen erfüllt. */
export interface DisplayResult {
  result: CheckResult;
  reason?: CheckReason;
  person?: PersonBrief;
  candidates?: Candidate[];
  minutesSinceLastCheck?: number;
  /** true = auf dem Gerät entschieden, weil der Server nicht antwortete. */
  offline?: boolean;
}

/** Quelle der lokalen Prüfung; null, solange kein Abzug vorliegt. */
export interface LocalProvider {
  check(mode: CheckMode, query?: string, personId?: string): LocalOutcome | null;
}

export interface EventQueue {
  enqueue(event: {
    clientEventId: string;
    mode: string;
    query?: string;
    personId?: string;
    result: string;
    reason?: string;
    clientAt: string;
  }): Promise<void>;
}

/**
 * Zustand des Prüfbildschirms.
 *
 * `offline` ist bewusst ein eigener Zustand neben `result`: ein Netzfehler
 * darf niemals wie ROT aussehen. ROT ist ein Urteil über die Person,
 * ein Verbindungsabbruch eine Aussage über die Leitung.
 */
export type Phase =
  | { kind: 'idle' }
  | { kind: 'pending'; query: string }
  | { kind: 'result'; query: string; response: DisplayResult }
  // Die Art reist mit: die Wiederholung muss dieselbe sein wie der erste
  // Versuch, sonst steht im Protokoll eine Zifferneingabe, wo gescannt wurde.
  | { kind: 'offline'; query: string; mode: CheckMode }
  | { kind: 'error'; query: string; mode: CheckMode; message: string }
  | { kind: 'locked-out' };

/** Rückmeldung an die Duty-Kraft. In Tests ersetzbar. */
export interface Feedback {
  green(): void;
  yellow(): void;
  red(): void;
}

export interface SessionOptions {
  /** Wie lange ein GRÜN stehen bleibt, bevor der Bildschirm freimacht. */
  /** Fenster, in dem dieselbe Eingabe als Doppelauslösung gilt. */
  dedupeMs?: number;
  /**
   * Frist für die Serverantwort. Danach entscheidet das Gerät selbst.
   *
   * Bewusst server-zuerst statt lokal-zuerst (Korrektur an docs/02 §5):
   * bei intaktem Netz antwortet der Server in etwa 150 ms, das merkt
   * niemand. Lokal zuerst zu entscheiden und später zu korrigieren hiesse,
   * ein GRÜN nach zwei Sekunden zu ROT zu machen. Da ist die Person
   * längst durch.
   */
  deadlineMs?: number;
  local?: LocalProvider;
  queue?: EventQueue;
  now?: () => number;
  newId?: () => string;
}

const DEFAULTS = { dedupeMs: 1500, deadlineMs: 1200 };
const TIMEOUT = Symbol('deadline');

export class CheckSession {
  readonly phase: Signal<Phase> = signal<Phase>({ kind: 'idle' });

  /**
   * Laufende Nummer jeder Anfrage. Trifft eine Antwort ein, deren Nummer
   * nicht die aktuelle ist, wird sie verworfen.
   *
   * Der Fall dahinter: Duty scannt Karte A, das Netz hängt, sie scannt
   * Karte B. Träfe die Antwort zu A danach ein, stünde GRÜN mit dem Namen
   * von A auf dem Bildschirm, während B davorsteht.
   */
  private seq = 0;
  private lastSubmit: { key: string; at: number } | null = null;

  /** Art der letzten Anfrage. Die Bestätigung eines Kandidaten erbt sie. */
  private lastMode: CheckMode = 'id';

  private readonly dedupeMs: number;
  private readonly deadlineMs: number;
  private readonly local?: LocalProvider;
  private readonly queue?: EventQueue;
  private readonly now: () => number;
  private readonly newId: () => string;

  constructor(
    private readonly api: Api,
    private readonly feedback: Feedback,
    options: SessionOptions = {},
  ) {
    this.dedupeMs = options.dedupeMs ?? DEFAULTS.dedupeMs;
    this.deadlineMs = options.deadlineMs ?? DEFAULTS.deadlineMs;
    this.local = options.local;
    this.queue = options.queue;
    this.now = options.now ?? (() => Date.now());
    this.newId = options.newId ?? (() => crypto.randomUUID());
  }

  /** Prüfung anstoßen. Leere Eingaben und Doppelauslösungen werden verworfen. */
  async submit(mode: CheckMode, query: string): Promise<void> {
    const trimmed = query.trim();
    if (!trimmed) return;

    // Ein Barcode-Scanner feuert denselben Code gern mehrfach, solange er
    // im Bild ist. Ohne diese Sperre stünden zwei Einträge im Protokoll.
    const key = `${mode}:${trimmed}`;
    if (this.lastSubmit && this.lastSubmit.key === key && this.now() - this.lastSubmit.at < this.dedupeMs) {
      return;
    }
    this.lastSubmit = { key, at: this.now() };
    this.lastMode = mode;

    await this.run({ mode, query: trimmed, clientEventId: this.newId(), clientAt: new Date().toISOString() }, trimmed);
  }

  /**
   * Einen Kandidaten aus einer GELB-Antwort bestätigen.
   *
   * Die Bestätigung erbt die Art der ursprünglichen Anfrage. Fest `name` zu
   * schreiben war richtig, solange GELB fast immer aus der Namenssuche kam;
   * seit die entfallen ist, entsteht GELB nur noch über die lose ID-Form.
   * und das Protokoll würde eine Namenssuche behaupten, die es nicht gab.
   */
  async confirm(candidate: Candidate): Promise<void> {
    const label = `${candidate.firstName} ${candidate.lastName}`.trim();
    this.lastSubmit = null;
    await this.run(
      {
        mode: this.lastMode,
        personId: candidate.personId,
        clientEventId: this.newId(),
        clientAt: new Date().toISOString(),
      },
      label,
    );
  }

  private async run(request: CheckRequest, label: string): Promise<void> {
    const mine = ++this.seq;
    this.phase.value = { kind: 'pending', query: label };

    const serverCall = this.api.check(request);
    const outcome = await Promise.race([
      serverCall,
      new Promise<typeof TIMEOUT>((resolve) => setTimeout(() => resolve(TIMEOUT), this.deadlineMs)),
    ]);

    // Verspätete Antwort auf eine ältere Anfrage: verwerfen.
    if (mine !== this.seq) return;

    // Ein gesperrtes Gerät hilft auch offline nicht weiter.
    if (outcome !== TIMEOUT && !outcome.ok && outcome.kind === 'unauthorized') {
      this.phase.value = { kind: 'locked-out' };
      return;
    }

    let display: DisplayResult | null = null;

    if (outcome !== TIMEOUT && outcome.ok) {
      display = outcome.data;
    } else {
      // Server zu langsam oder nicht erreichbar: das Gerät entscheidet selbst.
      const local = this.local?.check(request.mode, request.query, request.personId) ?? null;

      if (local) {
        display = { ...local, offline: true };
        // Nur einreihen, wenn der Server die Prüfung tatsächlich nicht
        // bekommen hat, sonst stünde sie doppelt im Protokoll.
        void serverCall.then((late) => {
          if (!late.ok) {
            void this.queue?.enqueue({
              clientEventId: request.clientEventId,
              mode: request.mode,
              query: request.query,
              personId: request.personId,
              result: local.result,
              reason: local.reason,
              clientAt: request.clientAt,
            });
          }
        });
      }
    }

    if (mine !== this.seq) return;

    if (!display) {
      // Weder Server noch Abzug: nichts geprüft. Bewusst nicht ROT.
      this.phase.value =
        outcome !== TIMEOUT && !outcome.ok && outcome.kind === 'error'
          ? { kind: 'error', query: label, mode: request.mode, message: outcome.message }
          : { kind: 'offline', query: label, mode: request.mode };
      return;
    }

    this.phase.value = { kind: 'result', query: label, response: display };

    // Jedes Ergebnis bleibt stehen, bis es quittiert wird. Auch GRÜN.
    //
    // Bis zum 03.09.2026 gab GRÜN sich nach zwei Sekunden selbst frei, um
    // die Schlange in Bewegung zu halten. Am Gerät führte das zu einem
    // Kreislauf: die Karte liegt nach dem Scan noch im Bild, die Sperre
    // gegen Doppelscans hält nur 1,5 Sekunden, und die halbe Sekunde
    // dazwischen reicht für die nächste Prüfung. Der Bildschirm flackerte,
    // das Gerät vibrierte im Takt, und jede Runde stand als eigener Eintrag
    // im Protokoll. Ein Knopf ist langsamer und dafür eindeutig.
    if (display.result === 'green') {
      this.feedback.green();
    } else if (display.result === 'yellow') {
      this.feedback.yellow();
    } else {
      this.feedback.red();
    }
  }

  /**
   * Jedes Ergebnis wird von Hand weggetippt. Durchwinken soll eine bewusste
   * Handlung sein, kein Ablauf einer Zeitspanne.
   *
   * Die Sperre gegen Doppelscans wird dabei zurückgesetzt: wer quittiert
   * hat, ist mit dieser Person fertig. Hält jemand dieselbe Karte gleich
   * noch einmal hin, ist das eine neue Prüfung und keine Wiederholung.
   */
  dismiss(): void {
    this.lastSubmit = null;
    this.seq++;
    this.phase.value = { kind: 'idle' };
  }

}
